import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { toast } from 'react-hot-toast';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { NotificationPreferences } from '@/types/auth';

interface NotificationCategory {
  id: keyof NotificationPreferences;
  label: string;
  description: string;
  items: {
    id: string;
    label: string;
    description: string;
  }[];
}

const categories: NotificationCategory[] = [
  {
    id: 'marketing',
    label: 'Marketing',
    description: 'Product updates and news',
    items: [
      {
        id: 'product_updates',
        label: 'Product Updates',
        description: 'Learn about new features and improvements',
      },
      {
        id: 'newsletters',
        label: 'Newsletters',
        description: 'Industry insights and best practices',
      },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    description: 'Important account notifications',
    items: [
      {
        id: 'billing_notifications',
        label: 'Billing Notifications',
        description: 'Payment receipts and billing reminders',
      },
    ],
  },
];

export function NotificationSettings() {
  const queryClient = useQueryClient();

  // Fetch notification preferences
  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: () => authApi.getNotificationPreferences(),
  });

  // Update preferences mutation
  const updateMutation = useMutation({
    mutationFn: (data: NotificationPreferences) => authApi.updateNotificationPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
      toast.success('Notification preferences saved');
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to save notification preferences');
    },
  });

  const handleToggle = (categoryId: keyof NotificationPreferences, itemId: string) => {
    if (!preferences) return;

    const updatedPreferences: NotificationPreferences = {
      ...preferences,
      [categoryId]: {
        ...preferences[categoryId],
        [itemId]: !preferences[categoryId][itemId as keyof typeof preferences[typeof categoryId]],
      },
    };

    updateMutation.mutate(updatedPreferences);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load notification preferences. Please try again.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Notification Settings</h2>
      <p className="text-gray-600 mb-6">
        Choose which email notifications you'd like to receive
      </p>

      <div className="space-y-8">
        {categories.map(category => (
          <div key={category.id} className="border-b border-gray-200 pb-6 last:border-0">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">{category.label}</h3>
              <p className="text-sm text-gray-600">{category.description}</p>
            </div>

            <div className="space-y-4">
              {category.items.map(item => {
                const isEnabled = preferences?.[category.id]?.[item.id as keyof typeof preferences[typeof category.id]] ?? false;

                return (
                  <div key={item.id} className="flex items-start justify-between">
                    <div className="flex-1 pr-4">
                      <label htmlFor={item.id} className="text-sm font-medium text-gray-900">
                        {item.label}
                      </label>
                      <p className="text-sm text-gray-500">{item.description}</p>
                    </div>

                    {/* Toggle Switch */}
                    <button
                      id={item.id}
                      type="button"
                      onClick={() => handleToggle(category.id, item.id)}
                      disabled={updateMutation.isPending}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      } ${updateMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                      role="switch"
                      aria-checked={isEnabled}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          isEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Email Preferences Note */}
      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-sm text-gray-600">
          <strong>Note:</strong> Critical security alerts and important account notifications cannot be disabled and will always be sent to your registered email address.
        </p>
      </div>
    </div>
  );
}
